import { useState } from "react";

import { useNavigate, useSearchState } from "common/router/hooks";
import { parseNumber } from "common/utils/numbers";
import { CORE_PHONE_LENGTH } from "common/constants";

import PhoneInput from "client/common/components/inputs/PhoneInput";
import Form from "client/common/components/forms/Form";
import Button from "client/common/components/buttons/Button";
import TextButton from "client/common/components/buttons/TextButton";
import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import { useLoginPhoneNumberValidateQuery, useProfileLookupMutation } from "client/accounts/hooks";
import { IProfileLookupResult } from "client/accounts/types";
import { Paths, urls } from "client/routes";

import SmsLegalDisclaimer from "./SmsLegalDisclaimer";
import AccountCreationLegalDisclaimer from "./AccountCreationDisclaimer";

import styles from "../../styles/PhoneNumberLookupForm.module.less";

type PhoneNumberLookupFormProps = {
  onSuccess: (result: IProfileLookupResult) => void;
  initialValue?: string;
};

const genPhoneInputStatus = (phoneQuery: ReturnType<typeof useLoginPhoneNumberValidateQuery>) => {
  if (phoneQuery.isLoading) {
    return "loading";
  }
  if (phoneQuery.isSuccess) {
    return phoneQuery.data?.is_valid ? "success" : "error";
  }
  return "idle";
};

const PhoneNumberLookupForm = (props: PhoneNumberLookupFormProps) => {
  const navigate = useNavigate();
  const { onSuccess, initialValue } = props;

  const [value, setValue] = useState(initialValue || "");

  const isPhoneComplete = value.length >= CORE_PHONE_LENGTH;
  const validateQuery = useLoginPhoneNumberValidateQuery(
    { number: value },
    { enabled: isPhoneComplete },
  );
  const {
    data: { is_valid: isValid = true, detail: validationDetail = "" } = {},
    error: validationError,
  } = validateQuery;

  const {
    mutate: lookupProfile,
    isPending: isLookingUp,
    isSuccess: isLookupSuccess,
    error: lookupError,
  } = useProfileLookupMutation({
    onSuccess,
  });

  const error = lookupError || validationError;
  const validationResponseErrorMessages = !isValid && validationDetail ? [validationDetail] : [];

  const canSubmit = isPhoneComplete && !(isLookingUp || isLookupSuccess) && isValid;
  const isSubmitting = isLookingUp || isLookupSuccess;
  const onSubmit = () => {
    lookupProfile({ number: parseNumber(value, 0), check_sms: true });
  };

  const [search] = useSearchState<Paths["accounts"]["lookup"]>({});

  return (
    <Form className={styles.form} canSubmit={canSubmit} onSubmit={onSubmit}>
      {error && <FetchErrorAlert error={error} />}
      <PhoneInput
        label="Mobile Number"
        value={value}
        onChange={setValue}
        status={genPhoneInputStatus(validateQuery)}
        errors={lookupError?.errors.number || validationResponseErrorMessages}
      />
      <Button disabled={!canSubmit} isLoading={isSubmitting} type="submit">
        Next
      </Button>
      <div className={styles.help}>
        Click{" "}
        <TextButton
          hasUnderline={true}
          onClick={() => {
            navigate(urls.accounts.lookup({ search: { ...search, method: "email" } }));
          }}
        >
          here
        </TextButton>{" "}
        to instead look up an account by email.
      </div>
      <AccountCreationLegalDisclaimer textAlign="center" />
      <SmsLegalDisclaimer textAlign="center" />
    </Form>
  );
};

export default PhoneNumberLookupForm;
